import { useEffect, useMemo, useState } from 'react';
import VectorTileLayer from 'ol/layer/VectorTile';
import { toFeature } from 'ol/render/Feature';
import { getLegendCanvasItemFromStyle, useLayer, useOLMap } from '@tmapy/mapcore';
import { useDispatch, useSelector } from '@tmapy/redux';
import { actionUpdateLegendState } from '../actions';
import { useMessage } from '@tmapy/intl';
const isVectorTileLayer = (layer) => {
    return layer instanceof VectorTileLayer;
};
const getFeatureGeometryTypes = (features) => {
    const geometryTypes = [];
    features.forEach((feature) => {
        const currentType = feature.getGeometry()?.getType();
        // eliminate same legend symbol for MULTI geometry
        const isSameGeometryType = !!geometryTypes.find((geometry) => geometry.type?.toString().toUpperCase().replace('MULTI', '') ===
            currentType?.toString().toUpperCase().replace('MULTI', ''));
        if (!isSameGeometryType && currentType) {
            geometryTypes.push({ type: currentType, feature });
        }
    });
    return geometryTypes;
};
export const useVectorLegend = (layerId) => {
    const dispatch = useDispatch();
    const formatMessage = useMessage();
    const map = useOLMap();
    const layer = useLayer(layerId);
    const [legendData, setLegendData] = useState([]);
    const { zoom, center } = useSelector((state) => state.mapCore.view);
    const legendConfig = useSelector((state) => state.mapCore.layers).find((layer) => layerId === layer.id).legend;
    const legends = useSelector((state) => state.legend);
    const cachedLegend = legends.find((legend) => legend.layerId === layerId)
        ?.items;
    const legendUrl = useMemo(() => {
        if (cachedLegend?.length || !layer)
            return null;
        const extent = map.getView().calculateExtent();
        const features = isVectorTileLayer(layer)
            ? (layer.getFeaturesInExtent?.(extent) ?? [])
            : (layer.getSource()?.getFeaturesInExtent?.(extent) ?? []);
        const layerFeatureTypes = getFeatureGeometryTypes(features);
        if (layer && layerFeatureTypes.length) {
            const legendItems = [];
            const style = layer.getStyle() ?? layer.getStyleFunction();
            if (legendConfig) {
                layerFeatureTypes.forEach((featureGeometryType) => {
                    legendConfig.forEach((legendItem) => {
                        const feature = isVectorTileLayer(layer)
                            ? toFeature(featureGeometryType.feature)
                            : featureGeometryType.feature.clone?.();
                        Object.entries(legendItem.props).forEach(([name, value]) => feature.set(name, value));
                        const canvas = getLegendCanvasItemFromStyle(feature, style);
                        if (canvas) {
                            legendItems.push({ url: canvas.toDataURL(), id: legendItem.id });
                        }
                    });
                });
            }
            else {
                layerFeatureTypes.forEach((featureGeometryType) => {
                    const canvas = getLegendCanvasItemFromStyle(featureGeometryType.feature, style);
                    if (canvas) {
                        legendItems.push({ url: canvas.toDataURL(), id: featureGeometryType.type });
                    }
                });
            }
            return legendItems;
        }
        return null;
    }, [cachedLegend, layer, map, zoom, center]);
    useEffect(() => {
        const legendItems = [];
        if (legendUrl) {
            legendUrl.map((img) => legendItems.push({
                imgSrc: img.url,
                label: formatMessage.fallback([`legend.${layerId}.${img.id}`, `legend.${layerId}`]) || layerId,
            }));
            setLegendData(legendItems);
            dispatch(actionUpdateLegendState({ layerId, legendItems }));
        }
    }, [dispatch, formatMessage, layerId, legendUrl]);
    return cachedLegend?.length ? cachedLegend : legendData;
};
//# sourceMappingURL=useVectorLegend.js.map